import React, { useState, useEffect } from "react";
import {
  Route,
  Routes,
  Link as RouterLink,
  useNavigate,
  useLocation,
} from "react-router-dom";
import jwtDecode, { JwtPayload } from "jwt-decode";
import "./App.css";
import AuthService from "./services/auth.service";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import "notyf/notyf.min.css";
import OrdersChatList from "./components/OrdersChatList";

import Login from "./pages/Login";
import Register from "./pages/Register";
import CreateOrder from "./pages/CreateOrder";
import CreateOrderByCoverageArea from "./pages/CreateOrderByCoverageArea";
import Orders from "./pages/Orders";
import ShowOrder from "./pages/ShowOrder";
import NotFound from "./pages/NotFound";
import Profile from "./pages/Profile";
import CoverageArea from "./pages/CoverageArea";
import Payment from "./pages/Payment";
import Reports from "./pages/Reports";
// import NewPayment from "./pages/NewPayment";
import { CreatePayment } from "./pages/payments/create-payment";

// Material UI
import {
  Box,
  CssBaseline,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  AppBar,
  Toolbar,
  Typography,
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
  IconButton,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { grey, red } from "@mui/material/colors";
import { AccountCircle } from "@mui/icons-material";
import AppContext from "./contexts/AppContext";
import { configAPIHeaders } from "./services/api";
import functionsUser from "./services/user.service";

import Logo from "./assets/images/logo.png";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// interface MatchProps extends RouteComponentProps<MatchParams> {};

interface CustomJwtPayload extends JwtPayload {
  id: string;
  email?: string;
}

// A custom hook that builds on useLocation to parse
// the query string for you.
const useQuery = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

interface AppInterface {
  name?: string;
  image?: string;
  color?: string;
}

const App = () => {
  let query = useQuery();
  const [app, setApp] = useState<AppInterface>();
  const [isLoadingApp, setIsLoadingApp] = useState<boolean>(true);

  // THEME
  let theme = createTheme({
    palette: {
      primary: {
        main:
          app !== null && app !== undefined && app.color !== undefined
            ? app.color
            : red[700],
        contrastText: "#FFF",
      },
      secondary: {
        main: grey[600],
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: null,
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);

  const [tokenValue, setTokenValue] = useState<string | null>(null);
  const [loadingLoginValue, setLoadingLoginValue] = useState<boolean | null>(
    true
  );
  const [userIDValue, setUserIDValue] = useState<string | null>(null);
  const [userEmailValue, setUserEmailValue] = useState<string | null>(null);
  const appContext = {
    token: tokenValue,
    setTokenValue,

    loadingLogin: loadingLoginValue,
    setLoadingLoginValue,

    userID: userIDValue,
    setUserIDValue,

    userEmail: userEmailValue,
    setUserEmailValue,
  };

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const history = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setDrawerOpen(open);
    };

  const logOut = () => {
    setLoadingLoginValue(true);
    AuthService.Logout();
    history("/login");
    setLoadingLoginValue(true);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const splashScreenHandler = async () => {
      let tokenIsAlive = true;
      try {
        let tempToken = localStorage.getItem("token");
        if (tempToken !== null && tempToken !== "" && tempToken !== "") {
          const token: string = tempToken;
          // console.log(token);
          const decoded = jwtDecode<CustomJwtPayload>(token);
          // console.log(JSON.stringify(decoded));

          const now: number = new Date().getTime();

          if (decoded !== undefined) {
            if (decoded.exp !== undefined) {
              // console.log("decoded !== undefined")
              decoded.exp = decoded.exp * 1000;

              if (now < decoded.exp) {
                configAPIHeaders(appContext, tempToken, decoded.id);
                appContext.setTokenValue(tempToken);
                appContext.setUserIDValue(decoded.id);
                appContext.setUserEmailValue(decoded.email ?? "teste");

                // await getUserByUserID(appContext, decoded.id);
                setLoadingLoginValue(false);

                // functionsUser.getUserApp()
                // .then((response) => {
                // 	setApp(response.data.app);
                // }).catch(error => {
                // 	console.log(error);
                // })
              } else {
                setLoadingLoginValue(false);
                // REFRESH TOKEN
              }
            } else {
              configAPIHeaders(appContext, tempToken, decoded.id);
              appContext.setTokenValue(tempToken);
              appContext.setUserIDValue(decoded.id);
              appContext.setUserEmailValue(decoded.email ?? "teste");

              // await getUserByUserID(appContext, decoded.id);
              setLoadingLoginValue(false);
            }
          } else {
            // console.log("decoded === undefined");
            tokenIsAlive = false;
          }
        } else {
          tokenIsAlive = false;
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (!tokenIsAlive) {
          appContext.setTokenValue(null);
          appContext.setLoadingLoginValue(false);

          localStorage.clear();
        }
      }
    };

    splashScreenHandler();
  }, []);

  useEffect(() => {
    if (query.get("APP_ID") !== null && query.get("APP_ID") !== undefined) {
      functionsUser
        .getApp(query.get("APP_ID")?.toString() ?? "")
        .then((response) => {
          setApp(response.data.app);
          // theme.palette.primary = response.data.app.color;
          // setIsLoadingApp(false);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoadingApp(false);
        });
    } else {
      setIsLoadingApp(false);
    }
  }, [query]);

  // useEffect(() => {
  // 	console.log(userIDValue);
  // }, [userIDValue]);

  return (
    <>
      <ThemeProvider theme={theme}>
        {isLoadingApp === true ? (
          <>
            <Box
              style={{
                justifyContent: "center",
                alignContent: "center",
                height: "90vh",
                display: "flex",
              }}
            >
              <CircularProgress size={100} style={{ alignSelf: "center" }} />
            </Box>
          </>
        ) : (
          <>
            <AppContext.Provider value={appContext}>
              <ScrollToTop />
              <React.Fragment>
                <CssBaseline />
                <AppBar position="sticky" component={"nav"}>
                  <Toolbar>
                    <IconButton
                      size={"large"}
                      edge="start"
                      color={"inherit"}
                      aria-label="menu"
                      sx={{ mr: 2 }}
                      onClick={toggleDrawer(true)}
                    >
                      <MenuIcon />
                    </IconButton>
                    <img
                      src={Logo}
                      alt="Leve Aí"
                      style={{ maxHeight: 35, marginRight: 10 }}
                    />
                    <Typography
                      variant="h6"
                      component={"div"}
                      sx={{ flexGrow: 1 }}
                      whiteSpace={"nowrap"}
                      overflow={"hidden"}
                      textOverflow={"ellipsis"}
                    >
                      {app !== null &&
                      app !== undefined &&
                      app.name !== undefined &&
                      app.name !== null
                        ? app.name
                        : "Leve Aí - Parceiros"}
                    </Typography>
                    {appContext.token ? (
                      <>
                        <div>
                          <IconButton
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            color="inherit"
                          >
                            <AccountCircle />
                          </IconButton>
                          <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            open={open}
                            onClose={() => {
                              handleClose();
                            }}
                          >
                            <MenuItem disabled>
                              {appContext?.userEmail ?? "-/-"}
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                history("/payment");
                                handleClose();
                              }}
                            >
                              Pagamento
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                history("/profile");
                                handleClose();
                              }}
                            >
                              Meu perfil
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                logOut();
                                handleClose();
                              }}
                            >
                              Desconectar
                            </MenuItem>
                          </Menu>
                        </div>
                      </>
                    ) : (
                      <>
                        <Button
                          color="inherit"
                          component={RouterLink}
                          to={"/login"}
                        >
                          Login
                        </Button>
                      </>
                    )}
                  </Toolbar>
                </AppBar>
                {/* <Toolbar /> */}
                <Drawer
                  anchor={"left"}
                  open={drawerOpen}
                  onClose={toggleDrawer(false)}
                >
                  <Box style={{ padding: 16, textAlign: "center" }}>
                    <Typography variant="h6" component={"h6"}>
                      {app !== null &&
                      app !== undefined &&
                      app.name !== undefined &&
                      app.name !== null
                        ? app.name
                        : "Leve Aí - Parceiros"}
                    </Typography>
                  </Box>
                  <div
                    role="presentation"
                    onClick={toggleDrawer(false)}
                    onKeyDown={toggleDrawer(false)}
                  >
                    {appContext.token ? (
                      <>
                        <Divider />
                        <ListSubheader
                          component="div"
                          id="nested-list-subheader"
                        >
                          Pedidos
                        </ListSubheader>
                        <ListItem button component={RouterLink} to={"/orders"}>
                          <ListItemIcon></ListItemIcon>
                          <ListItemText primary={"Meus pedidos"} />
                        </ListItem>
                        <ListItem
                          button
                          component={RouterLink}
                          to={"/coverageArea"}
                        >
                          <ListItemIcon></ListItemIcon>
                          <ListItemText primary={"Área de cobertura"} />
                        </ListItem>
                        <Divider />
                        <ListSubheader
                          component="div"
                          id="nested-list-subheader"
                        >
                          Minha conta
                        </ListSubheader>
                        <List>
                          <ListItem
                            button
                            onClick={() => {
                              history("/reports");
                              handleClose();
                            }}
                          >
                            <ListItemIcon></ListItemIcon>
                            <ListItemText primary={"Relatórios"} />
                          </ListItem>
                          <ListItem
                            button
                            onClick={() => {
                              history("/payment");
                              handleClose();
                            }}
                          >
                            <ListItemIcon></ListItemIcon>
                            <ListItemText primary={"Pagamento"} />
                          </ListItem>
                          <ListItem
                            button
                            onClick={() => {
                              history("/profile");
                              handleClose();
                            }}
                          >
                            <ListItemIcon></ListItemIcon>
                            <ListItemText primary={"Meu perfil"} />
                          </ListItem>
                          <ListItem
                            button
                            onClick={() => {
                              logOut();
                            }}
                          >
                            <ListItemIcon></ListItemIcon>
                            <ListItemText primary={"Desconectar"} />
                          </ListItem>
                        </List>
                      </>
                    ) : (
                      <>
                        <List>
                          <ListItem button component={RouterLink} to={"/login"}>
                            <ListItemIcon></ListItemIcon>
                            <ListItemText primary={"Login"} />
                          </ListItem>
                        </List>
                      </>
                    )}
                  </div>
                </Drawer>
                <Box>
                  <div>
                    <div
                      className="mt-5 mb-5"
                      style={{ marginBottom: 5, marginTop: 5 }}
                    >
                      {appContext === null ||
                      appContext === undefined ||
                      appContext.loadingLogin === undefined ||
                      appContext.loadingLogin === true ||
                      appContext.loadingLogin === null ? (
                        <>
                          <Box
                            style={{
                              justifyContent: "center",
                              alignContent: "center",
                              height: "90vh",
                              display: "flex",
                            }}
                          >
                            <CircularProgress
                              size={100}
                              style={{ alignSelf: "center" }}
                            />
                          </Box>
                        </>
                      ) : (
                        <>
                          {appContext.token === undefined ||
                          appContext.token === null ||
                          appContext.token === "" ? (
                            <>
                              <Routes>
                                <Route path="/" element={<Login />} />
                                <Route path="/login" element={<Login />} />
                                <Route
                                  path="/register"
                                  element={<Register />}
                                />
                                <Route path="/*" element={<NotFound />} />
                              </Routes>
                            </>
                          ) : (
                            <>
                              <Routes>
                                <Route path={"/"} element={<Orders />} />
                                <Route path={"/home"} element={<Orders />} />
                                <Route
                                  path={"/reports"}
                                  element={<Reports />}
                                />
                                <Route
                                  path={"/payment"}
                                  element={<Payment />}
                                />
                                <Route
                                  path={"/payment/new"}
                                  element={<CreatePayment />}
                                />
                                <Route
                                  path={"/profile"}
                                  element={<Profile />}
                                />
                                <Route
                                  path={"/coverageArea"}
                                  element={<CoverageArea />}
                                />
                                <Route path="/orders" element={<Orders />} />
                                <Route
                                  path="/v2/order/create"
                                  element={<CreateOrderByCoverageArea />}
                                />
                                <Route
                                  path="/order/create"
                                  element={<CreateOrder />}
                                />
                                <Route
                                  path="/order/:id"
                                  element={<ShowOrder />}
                                />
                                <Route path="/*" element={<NotFound />} />
                              </Routes>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Box>
                {appContext &&
                appContext.token !== undefined &&
                appContext.token !== null ? (
                  <OrdersChatList />
                ) : (
                  <></>
                )}
              </React.Fragment>
            </AppContext.Provider>
          </>
        )}
      </ThemeProvider>
    </>
  );
};

const AppWithProviders = () => {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
};

export default AppWithProviders;
